import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, LogOut, ShieldCheck } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { UploadPanel } from "@/components/admin/UploadPanel";
import { BatchList } from "@/components/admin/BatchList";
import { ReviewGrid } from "@/components/admin/ReviewGrid";
import { BannerReview } from "@/components/admin/BannerReview";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [deletingBatchId, setDeletingBatchId] = useState(null);

  const loadBatches = useCallback(async () => {
    try {
      const response = await api.get("/admin/imports");
      setBatches(response.data);
      setSelectedBatch((current) => response.data.find((batch) => batch.id === current?.id) || response.data[0] || null);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not load imports");
    }
  }, []);

  const loadBatchDetails = useCallback(async (batch) => {
    if (!batch) {
      setProducts([]);
      setBanners([]);
      return;
    }
    try {
      const [productResponse, bannerResponse, batchResponse] = await Promise.all([
        api.get(`/admin/imports/${batch.id}/products`),
        api.get(`/admin/imports/${batch.id}/banners`),
        api.get(`/admin/imports/${batch.id}`),
      ]);
      setProducts(productResponse.data);
      setBanners(bannerResponse.data);
      setSelectedBatch(batchResponse.data);
      setBatches((current) => current.map((item) => (item.id === batch.id ? batchResponse.data : item)));
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not load review data");
    }
  }, []);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const selectedBatchId = selectedBatch?.id;

  useEffect(() => {
    if (selectedBatchId) {
      loadBatchDetails({ id: selectedBatchId });
    } else {
      setProducts([]);
      setBanners([]);
    }
  }, [selectedBatchId, loadBatchDetails]);

  useEffect(() => {
    const hasProcessing = batches.some((batch) => ["uploaded", "processing"].includes(batch.status));
    if (!hasProcessing) return undefined;
    const timer = setInterval(() => {
      loadBatches();
      if (selectedBatchId) loadBatchDetails({ id: selectedBatchId });
    }, 4000);
    return () => clearInterval(timer);
  }, [batches, loadBatches, loadBatchDetails, selectedBatchId]);

  const stats = useMemo(
    () => ({
      products: selectedBatch?.product_count || products.length,
      banners: selectedBatch?.banner_count || banners.length,
      published: selectedBatch?.published_count || 0,
      filtered: products.filter((product) => product.quantity_filtered).length,
    }),
    [selectedBatch, products, banners],
  );

  const handleUploaded = async (batch) => {
    await loadBatches();
    setSelectedBatch(batch);
  };

  const handleDeleteBatch = async (batch) => {
    const confirmed = window.confirm(
      `Delete "${batch.original_filename}"? This will also remove its products, banners, published catalog entries, and images.`,
    );
    if (!confirmed) return;
    setDeletingBatchId(batch.id);
    try {
      const response = await api.delete(`/admin/imports/${batch.id}`);
      toast.success(
        `Import batch deleted (${response.data.staged_products_deleted} products, ${response.data.banners_deleted} banners removed)`,
      );
      if (selectedBatchId === batch.id) {
        setSelectedBatch(null);
        setProducts([]);
        setBanners([]);
      }
      await loadBatches();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not delete import batch");
    } finally {
      setDeletingBatchId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100" data-testid="admin-dashboard">
      <header className="sticky top-0 z-20 border-b border-slate-800 bg-slate-950/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1800px] flex-col gap-4 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-blue-300">
              <ShieldCheck className="h-4 w-4" /> BusinessBox.co.in Admin Engine
            </div>
            <h1 className="mt-2 font-headline text-3xl font-bold text-white">Intelligent Import & Review</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/admin/features" data-testid="admin-features-link" className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-blue-400 hover:text-blue-300">
              Manage features
            </Link>
            <Link to="/admin/categories" data-testid="admin-categories-link" className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-blue-400 hover:text-blue-300">
              Manage categories
            </Link>
            <Link to="/" data-testid="admin-catalog-link" className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-blue-400 hover:text-blue-300">
              View live catalog
            </Link>
            <span className="text-sm text-slate-400" data-testid="admin-user-email">{user?.email}</span>
            <Button data-testid="admin-logout-button" variant="outline" onClick={logout} className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800">
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1800px] space-y-6 px-4 py-6">
        <UploadPanel onUploaded={handleUploaded} />
        <div className="grid gap-4 md:grid-cols-4" data-testid="admin-metrics-panel">
          {[
            ["Staged products", stats.products],
            ["Banner candidates", stats.banners],
            ["Published products", stats.published],
            ["Quantity filtered", stats.filtered],
          ].map(([label, value]) => (
            <div key={label} className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5">
              <div className="flex items-center justify-between text-sm text-slate-400"><span>{label}</span><Boxes className="h-4 w-4 text-blue-300" /></div>
              <div className="mt-3 font-headline text-3xl font-bold text-white">{value}</div>
            </div>
          ))}
        </div>
        <div className="grid gap-6 xl:grid-cols-[320px_1fr]">
          <BatchList batches={batches} selectedBatch={selectedBatch} onSelect={setSelectedBatch} onDelete={handleDeleteBatch} deletingBatchId={deletingBatchId} />
          <div className="space-y-6">
            <ReviewGrid batch={selectedBatch} products={products} onRefresh={() => selectedBatch && loadBatchDetails(selectedBatch)} />
            <BannerReview banners={banners} onRefresh={() => selectedBatch && loadBatchDetails(selectedBatch)} />
          </div>
        </div>
      </main>
    </div>
  );
}
